// Agent Service API
// LazySEM Frontend - AI Agent REST session management via Gateway
// All session endpoints route through: /api/v1/agent (gateway strips prefix → agent service)

import { authenticatedFetch } from "./authenticatedFetch";

const AGENT_PREFIX = "/api/v1/agent";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ChatSession {
  session_id: string;
  user_id: string;
  org_id: string;
  title?: string;
  created_at: string;
  updated_at?: string;
  message_count?: number;
}

export interface SessionMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface SessionDetail extends ChatSession {
  messages: SessionMessage[];
}

export interface CreateSessionRequest {
  user_id: string;
  org_id: string;
  title?: string;
}

// ── Plan types ──────────────────────────────────────────────────────────────
export interface PlanStep {
  step_number: number;
  description: string;
  service: string; // 'analytics' | 'monitoring' | 'briefing' | 'similarity'
  operation: "read" | "write";
  estimated_credits: number | null;
}

export interface ProposedPlan {
  reasoning: string;
  steps: PlanStep[];
  total_estimated_credits: number | null;
}

// Agent WebSocket message types (received from server)
export type AgentMessageType =
  | "RUN_STATUS"
  | "error"
  | "THINKING"
  | "TOOL_CALL"
  | "TOOL_RESULT"
  | "TOKEN_EXPIRED"
  | "AGENT"
  | "ERROR"
  | "DONE"
  | "PLAN_PROPOSED" // Agent proposes a multi-step plan for user approval
  | "PLAN_REJECTED"; // Server confirmed plan was rejected

export interface AgentWSMessage {
  type: AgentMessageType;
  content: string;
  session_id?: string;
  run_id?: string;
  run?: AgentRun;
  tool_name?: string;
  params?: Record<string, unknown>;
  result?: unknown;
  status_code?: number;
  retry_query?: string;
  plan?: ProposedPlan; // present when type === 'PLAN_PROPOSED'
  metadata?: {
    tool_name?: string;
    tool_input?: Record<string, unknown>;
    tool_output?: unknown;
    session_id?: string;
    [key: string]: unknown;
  };
}

// Local chat message representation (UI state)
export interface ChatMessage {
  id: string;
  type: "user" | AgentMessageType;
  content: string;
  timestamp: Date;
  persisted?: boolean; // UI provenance for merging a bounded latest-history window.
  metadata?: AgentWSMessage["metadata"];
}

// ============================================================================
// REST API FUNCTIONS (session history via gateway)
// ============================================================================

/**
 * Create a new agent chat session
 */
export async function createSession(
  userId: string,
  orgId: string,
): Promise<ChatSession> {
  return authenticatedFetch<ChatSession>(`${AGENT_PREFIX}/history/sessions`, {
    method: "POST",
    body: JSON.stringify({ user_id: userId, organization_id: orgId }),
    serviceName: "Agent",
  });
}

/**
 * List all sessions for the current user
 */
export async function listSessions(userId: string): Promise<ChatSession[]> {
  const organizationId = localStorage.getItem("current_organization_id");
  if (!organizationId) return [];
  return authenticatedFetch<ChatSession[]>(
    `${AGENT_PREFIX}/history/sessions?user_id=${encodeURIComponent(userId)}&organization_id=${encodeURIComponent(organizationId)}`,
    { serviceName: "Agent" },
  );
}

/**
 * Get a specific session with its messages
 */
export async function getSession(sessionId: string): Promise<SessionDetail> {
  return authenticatedFetch<SessionDetail>(
    `${AGENT_PREFIX}/history/sessions/${sessionId}`,
    { serviceName: "Agent" },
  );
}

export interface HistoryMessage {
  id: string;
  // Backend MessageType.value: "user" | "agent" | "thinking" | "tool_call"
  // | "tool_result" | "orchestrator" | "system".
  type: string;
  content: string;
  source?: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

/**
 * Map a persisted backend message type to the UI ChatMessage type.
 * Tool/thinking steps map to their step types so they render as collapsed
 * reasoning steps — NOT as user ("YOU") bubbles. Anything unknown that isn't
 * clearly the assistant falls back to 'user'.
 */
export function mapHistoryType(backendType: string): ChatMessage["type"] {
  switch ((backendType || "").toLowerCase()) {
    case "agent":
    case "orchestrator":
      return "AGENT";
    case "thinking":
      return "THINKING";
    case "tool_call":
      return "TOOL_CALL";
    case "tool_result":
      return "TOOL_RESULT";
    case "user":
    default:
      return "user";
  }
}

/**
 * Fetch the message history for an existing session.
 */
export async function getSessionMessages(
  sessionId: string,
  limit = 100,
  latest = false,
): Promise<HistoryMessage[]> {
  const res = await authenticatedFetch<{ messages: HistoryMessage[] }>(
    `${AGENT_PREFIX}/history/sessions/${sessionId}/messages?limit=${limit}${latest ? "&latest=true" : ""}`,
    { serviceName: "Agent" },
  );
  return res.messages ?? [];
}

/**
 * Delete a session
 */
export async function deleteSession(sessionId: string): Promise<void> {
  return authenticatedFetch<void>(
    `${AGENT_PREFIX}/history/sessions/${sessionId}`,
    {
      method: "DELETE",
      serviceName: "Agent",
    },
  );
}

/**
 * Build WebSocket URL for the agent service, routed through the gateway.
 *
 * Dev:  ws://localhost:5177/api/v1/agent/ws/... → Vite proxy → ws://localhost:8006/api/v1/ws/...
 * Prod: wss://seo-saas-production.up.railway.app/api/v1/agent/ws/... → Gateway WS proxy → agent
 *
 * In production Netlify CANNOT proxy WebSocket connections via its redirect rules,
 * so we must connect directly to the Railway gateway using VITE_API_URL.
 */
export function buildAgentWsUrl(clientId: string, sessionId: string): string {
  if (import.meta.env.DEV) {
    // Dev: Vite proxy handles the WebSocket upgrade at localhost
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/api/v1/agent/ws/${clientId}/${sessionId}`;
  }
  // Prod: bypass Netlify (no WS support) and connect directly to the Railway gateway
  const rawBase = (import.meta.env.VITE_API_URL || "")
    .trim()
    .replace(/\/+$/, "");
  const pageIsHttps = window.location.protocol === "https:";
  if (!rawBase) {
    // eslint-disable-next-line no-console
    console.error(
      "[agent-ws] VITE_API_URL is not set in production build — WebSocket cannot be opened.",
    );
  }
  // Force secure scheme when the page is HTTPS to avoid mixed-content silent block
  // (browser blocks ws:// from https:// page before any network row appears in DevTools).
  let wsBase = (rawBase || "http://localhost:8000")
    .replace(/^https:/i, "wss:")
    .replace(/^http:/i, pageIsHttps ? "wss:" : "ws:");
  const url = `${wsBase}/api/v1/agent/ws/${clientId}/${sessionId}`;
  // eslint-disable-next-line no-console
  console.info("[agent-ws] opening", url);
  return url;
}

export interface AgentRun {
  run_id: string;
  session_id: string;
  status:
    | "running"
    | "stopping"
    | "stopped"
    | "completed"
    | "failed"
    | "awaiting_approval";
  started_at: string;
  updated_at: string;
  finished_at: string | null;
  message?: string;
  plan?: ProposedPlan;
}
export interface AgentRunResponse {
  capabilities: { stop: boolean };
  run: AgentRun | null;
}
export const getSessionRun = (sessionId: string) =>
  authenticatedFetch<AgentRunResponse>(
    `${AGENT_PREFIX}/history/sessions/${encodeURIComponent(sessionId)}/run`,
    { serviceName: "Agent" },
  );
export const stopSessionRun = (sessionId: string, runId: string) =>
  authenticatedFetch<AgentRunResponse>(
    `${AGENT_PREFIX}/history/sessions/${encodeURIComponent(sessionId)}/runs/${encodeURIComponent(runId)}/stop`,
    { method: "POST", serviceName: "Agent" },
  );
export const updateSessionTitle = (sessionId: string, title: string) =>
  authenticatedFetch<void>(
    `${AGENT_PREFIX}/history/sessions/${encodeURIComponent(sessionId)}/title?title=${encodeURIComponent(title)}`,
    { method: "PUT", serviceName: "Agent" },
  );
export const getWorkspaceProfile = () =>
  authenticatedFetch<{ profile: Record<string, unknown> | null }>(
    `${AGENT_PREFIX}/autonomy/profile`,
    { serviceName: "Agent" },
  );
export function conversationTitle(content: string): string {
  return (
    content
      .replace(/^https?:\/\/\S+\s*/i, "")
      .replace(/\s+/g, " ")
      .trim() || content.trim()
  ).slice(0, 64);
}
export function isLegacyTitle(title?: string) {
  return (
    !title ||
    /^(chat[ \u2014-]*)?\d{4}[-/]\d{2}[-/]\d{2}/i.test(title) ||
    /^chat\s*[-\u2014]?\s*\d{1,2}\//i.test(title)
  );
}
